import {
  provideHttpClient,
  HTTP_INTERCEPTORS,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router, withViewTransitions } from '@angular/router';
import { routes } from './app.routes';
import { provideTransloco } from '@jsverse/transloco';
import { createErrorHandler, TraceService } from '@sentry/angular';
import { environment } from '../environments/environment';
import {
  LANGUAGE_INTERCEPTOR_API_BASE_URL,
  LANGUAGE_INTERCEPTOR_CONFIG,
  LanguageInterceptor,
  LanguageService,
} from '@catch/services';
import { provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web';
import Aura from '@primeuix/themes/aura';
import { providePrimeNG } from 'primeng/config';
import { TranslocoAppLoader } from './transloco-loader';
import { ErrorInterceptor } from './interceptors/error.interceptor';
import { AuthenticationInterceptor } from './platform/interceptors/authentication.interceptor';
import { isPlatformRoute, pathFromUrl } from './utils/platform-route.util';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimations(),
    provideRouter(
      routes,
      withViewTransitions({
        skipInitialTransition: true,
        onViewTransitionCreated: ({ transition }) => {
          const router = inject(Router);
          const targetUrl = router.currentNavigation()?.finalUrl;
          if (!targetUrl) {
            transition.skipTransition();
            return;
          }
          const currentPath = pathFromUrl(router.url);
          const targetPath = pathFromUrl(router.serializeUrl(targetUrl));
          if (
            currentPath === targetPath ||
            (isPlatformRoute(currentPath) && isPlatformRoute(targetPath))
          ) {
            transition.skipTransition();
          }
        },
      })
    ),
    providePrimeNG({
      theme: {
        preset: Aura,
      },
    }),
    provideHttpClient(withInterceptorsFromDi()),
    provideTransloco({
      config: {
        availableLangs: ['en-US', 'de-DE', 'fr-FR'],
        defaultLang: 'de-DE',
        reRenderOnLangChange: true,
        prodMode: environment.production,
      },
      loader: TranslocoAppLoader,
    }),
    LanguageService,
    {
      provide: ErrorHandler,
      useValue: createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [TraceService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthenticationInterceptor,
      multi: true,
    },
    {
      provide: LANGUAGE_INTERCEPTOR_API_BASE_URL,
      useValue: environment.apiBaseUrl,
    },
    {
      provide: LANGUAGE_INTERCEPTOR_CONFIG,
      useValue: { allowFrFromBrowser: true },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageInterceptor,
      multi: true,
    },
    provideLottieOptions({
      player: () => player,
    }),
  ],
};
